import { EzString } from '/ezlibrary/helpers/EzString.js';
import { EzEnumeration2 } from '/ezlibrary/enums/EzEnumeration2.js';

/**
 * @class
 * @extends {EzEnumeration2}
 * @description
 * Defines the available time-entry or break-entry rounding methods.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into non-enumeration classes with:
 *      import {
 *          // ... other enumeration classes ...
 *          EzRoundingMethod
 *      } from '/ezlibrary/enums/EzEnumerations.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into other enumeration classes or classes that cannot import EzEnumerations:
 *      import { EzRoundingMethod } from '/ezlibrary/enums/EzRoundingMethod.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Static access to methods, fields, and properties: EzRoundingMethod.{static property|static method|static field}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzRoundingMethod extends EzEnumeration2 {
    /**
     * @static
     * @public @method
     * Example static method that returns the enumeration property's data.display value.
     * If the enumPropertyValue is not a valid enumeration property value, then EzRoundingMethod.UNKNOWN.display is returned.
     *
     * @param {undefined|null|string} enumPropertyValue
     * A valid enumeration property value from EzRoundingMethod
     *
     * @returns {string}
     */
    static ezToDisplayName(enumPropertyValue) {
        return EzString.stringOrEmpty(
            EzString.hasLength(enumPropertyValue)
                ? EzRoundingMethod.ezEnumData(enumPropertyValue)?.display
                : EzRoundingMethod.ezEnumData(EzRoundingMethod.UNKNOWN)?.display);
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Enumeration Class Initialization Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @private @field
     * Stores the singleton instance of this enumeration class.
     *
     * @type {EzRoundingMethod}
     */
    static #ezRoundingMethodSingleton = null;

    /**
     * @static
     * @private @method
     * Builds the enumeration properties and values for EzClockerAccountType
     */
    static #ezInit() {
        this.#ezRoundingMethodSingleton.ezBuildEnumProp(
            // Enumeration Property Name
            'UNKNOWN',
            // Enumeration Property Value
            'UNKNOWN',
            // Enumeration Data Value
            {
                value: 'UNKNOWN',
                display: 'UNKNOWN',
                roundingMethod: 'No rounding performed'
            });

        this.#ezRoundingMethodSingleton.ezBuildEnumProp(
            'NONE',
            'NONE',
            {
                value: 'NONE',
                display: 'None',
                roundingMethod: 'No rounding performed.'
            });

        this.#ezRoundingMethodSingleton.ezBuildEnumProp(
            'NEAREST_5',
            'NEAREST_5',
            {
                value: 'NEAREST_5',
                display: '5 Minutes',
                roundingMethod: 'Round to the nearest 5 minutes.'
            });

        this.#ezRoundingMethodSingleton.ezBuildEnumProp(
            'NEAREST_6',
            'NEAREST_6',
            {
                value: 'NEAREST_6',
                display: '6 Minutes',
                roundingMethod: 'Round to the nearest 6 minutes.'
            });

        this.#ezRoundingMethodSingleton.ezBuildEnumProp(
            'NEAREST_15',
            'NEAREST_15',
            {
                value: 'NEAREST_15',
                display: '15 Minutes',
                roundingMethod: 'Round to the nearest 15 minutes.'
            });
    }

    /**
     * Static Initialization of EzExportAction
     */
    static {
        if (null == this.#ezRoundingMethodSingleton) {
            this.#ezRoundingMethodSingleton = new EzRoundingMethod();

            this.#ezInit();
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
